/**
 * useSuggestionExpectation (GDN-2614, audit option T1)
 *
 * Expectation-driven polling for AI-suggested billing codes.
 *
 * The note response carries a server-computed tri-state (`suggestion_status`)
 * plus a deadline (`suggestion_expected_by`). While the status is `expected`,
 * this hook refetches the encounter — the metabar's codes come from
 * `encounter.working_billing_claim` — on a 3s → 5s → 8s backoff, and stops the
 * moment there is nothing left to wait for.
 *
 * INDEPENDENT DEPLOYABILITY: when `suggestion_status` is absent (older backend)
 * the hook is inert. It never schedules a timer, never dispatches an
 * invalidation, and reports `isSupported: false` with every UI flag `false`, so
 * consumers render exactly what they rendered before GDN-2614.
 *
 * Stop conditions (any one ends the poll):
 *   1. `suggestion_status === 'ready'`
 *   2. `suggestion_status === 'not_expected'`
 *   3. the field is absent / unrecognised (never starts)
 *   4. the deadline elapses — `suggestion_expected_by` + 60s grace
 *   5. the provider edited the working claim this session (U3 suppression)
 *   6. `enabled: false`, or no note/encounter id
 *   7. the codes arrived out-of-band — see `codesArrivedOutOfBand` below
 *
 * WHAT COUNTS AS AN EDIT FOR (5). `useWorkingClaimEdited`, not
 * `useWorkingClaimWritten` — accepting the model's own suggestion is agreement,
 * not an independent edit, and must not stop this poll. CPT recommendations and
 * ICD-10 suggestions come from separate pipelines on independent timing, so
 * accepting whichever resolves first would otherwise kill the other's shimmer,
 * badge, ghost-accept affordance and timeout-and-retry outright (GDN-2618).
 *
 * RESIDUAL: suppression is still keyed per *encounter*, not per code type, so a
 * genuine manual edit to either CPT or ICD-10 suppresses both. That is the
 * deliberate conservative U3 reading — see `workingClaimEditTracker` for why
 * narrowing it is a separate piece of work.
 *
 * OUT-OF-BAND ARRIVAL (GDN-2618). The note-generation stream now pushes the
 * claim the moment the backend authors it, well before the note's tri-state can
 * flip to `ready`. `codesArrivedOutOfBand` folds that push into this hook so
 * there is exactly one owner of the pending/arrival state: the poll stops, the
 * shimmer clears, and the arrival transition plays once — here, at the push —
 * rather than a second time when the later invalidation finally reports
 * `ready`.
 *
 * Note on what gets invalidated: the Encounter tag carries the codes, but the
 * tri-state itself lives on the note response — so the Note tag is invalidated
 * alongside it. Without that the client could never observe the flip to `ready`
 * and every note would run to the timeout.
 *
 * Why this is safe during note-generation streaming, and why NOT to "fix" it
 * with an `isGeneratingNote` guard: `useNoteGenerationStreaming` holds every
 * streamed value in component state and never touches the RTKQ cache, so a
 * Note invalidation cannot clobber in-flight streamed sections. The generation
 * pipeline already invalidates the Note tag mid-stream itself (on
 * `populate_sections` completion, see NoteChatView). Gating this hook on
 * `isGeneratingNote` would suppress polling during exactly the window in which
 * codes land for v1-template notes, which is worse than the churn it avoids.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWorkingClaimEdited } from '@features/encounters/hooks/useWorkingClaimEdited';
import { encountersApi } from '@features/encounters/store/api';
import { notesApi } from '@features/notes';
import { useAppDispatch } from '@store/hooks';

import type { SuggestionStatus } from '../suggestionContract';
import { readSuggestionExpectedBy, readSuggestionStatus } from '../suggestionContract';

/** Poll backoff. The final entry repeats until a stop condition fires. */
export const SUGGESTION_POLL_BACKOFF_MS = [3_000, 5_000, 8_000] as const;

/** Grace period past `suggestion_expected_by` before we call it a failure. */
export const SUGGESTION_TIMEOUT_GRACE_MS = 60_000;

/**
 * Fallback window used when the backend says `expected` but sends no
 * `suggestion_expected_by`. Anchored on the first `expected` observation.
 */
export const SUGGESTION_DEFAULT_WINDOW_MS = 60_000;

/** How long `justArrived` stays true so the arrival transition is noticed. */
export const SUGGESTION_ARRIVAL_HIGHLIGHT_MS = 4_000;

export interface UseSuggestionExpectationOptions {
  /** The loaded note response. May predate the suggestion contract. */
  note?: unknown;
  /** Note id — invalidated so the tri-state can advance. */
  noteId?: string | null;
  /** Encounter id — invalidated so the claim (and its codes) refresh. */
  encounterId?: string | null;
  /** Escape hatch; defaults to true. */
  enabled?: boolean;
  /**
   * The codes were delivered outside the poll — pushed over the note-generation
   * stream (GDN-2618). Treated as the arrival: the poll stops, `isPending`
   * clears, and `justArrived` fires once here instead of again on the later
   * `expected` → `ready` flip.
   */
  codesArrivedOutOfBand?: boolean;
}

export interface SuggestionExpectation {
  /** Whether this backend speaks the suggestion contract at all. */
  isSupported: boolean;
  /** The raw tri-state, or `null` when unsupported. */
  status: SuggestionStatus | null;
  /** `suggestion_expected_by` in epoch ms, or `null`. */
  expectedByMs: number | null;
  /** Instant at which we give up, in epoch ms. `null` when not waiting. */
  deadlineMs: number | null;
  /** Codes are in flight — render the pending affordance. */
  isPending: boolean;
  /** The deadline passed while still `expected` — render the failure state. */
  isTimedOut: boolean;
  /** Codes just landed — play the arrival transition. */
  justArrived: boolean;
  /** The provider edited the claim this session; suggestion UI stands down. */
  isSuppressed: boolean;
  /** The codes came from the stream push rather than from a poll (GDN-2618). */
  arrivedOutOfBand: boolean;
  /** Number of poll ticks issued (diagnostics/tests). */
  pollCount: number;
}

const INERT: SuggestionExpectation = {
  isSupported: false,
  status: null,
  expectedByMs: null,
  deadlineMs: null,
  isPending: false,
  isTimedOut: false,
  justArrived: false,
  isSuppressed: false,
  arrivedOutOfBand: false,
  pollCount: 0,
};

export function useSuggestionExpectation({
  note,
  noteId,
  encounterId,
  enabled = true,
  codesArrivedOutOfBand = false,
}: UseSuggestionExpectationOptions): SuggestionExpectation {
  const dispatch = useAppDispatch();

  const status = readSuggestionStatus(note);
  const isSupported = status !== null;
  const expectedByMs = readSuggestionExpectedBy(note);
  const isSuppressed = useWorkingClaimEdited(encounterId);

  const [pollCount, setPollCount] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const [justArrived, setJustArrived] = useState(false);
  const [arrivedOutOfBand, setArrivedOutOfBand] = useState(false);
  // Which out-of-band arrival we have already reacted to, so a re-render (or a
  // second pushed snapshot) can't replay the transition.
  const consumedOutOfBandRef = useRef(false);

  // Anchor for the fallback deadline: when we first saw `expected` for this note.
  const [expectedSince, setExpectedSince] = useState<{ noteId?: string | null; at: number } | null>(
    null,
  );

  useEffect(() => {
    if (status !== 'expected') {
      setExpectedSince(null);
      return;
    }
    setExpectedSince(prev => (prev && prev.noteId === noteId ? prev : { noteId, at: Date.now() }));
  }, [status, noteId]);

  // Reset per-note state when the screen switches notes.
  useEffect(() => {
    setTimedOut(false);
    setPollCount(0);
    setJustArrived(false);
    setArrivedOutOfBand(false);
    consumedOutOfBandRef.current = false;
  }, [noteId]);

  // Out-of-band arrival (GDN-2618): the stream pushed the claim. Own the
  // arrival here — stop the poll, clear pending, play the transition once.
  useEffect(() => {
    if (!codesArrivedOutOfBand || consumedOutOfBandRef.current) return undefined;
    consumedOutOfBandRef.current = true;

    // U3: the provider's edit already owns the screen. Consume the arrival so
    // it can never fire later, but neither animate nor un-suppress anything.
    if (isSuppressed) return undefined;

    setArrivedOutOfBand(true);
    setJustArrived(true);
    const timer = setTimeout(() => setJustArrived(false), SUGGESTION_ARRIVAL_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [codesArrivedOutOfBand, isSuppressed]);

  // Leaving `expected` clears any recorded timeout.
  useEffect(() => {
    if (status !== 'expected') setTimedOut(false);
  }, [status]);

  const deadlineMs = useMemo(() => {
    if (status !== 'expected') return null;
    const base = expectedByMs ?? (expectedSince ? expectedSince.at + SUGGESTION_DEFAULT_WINDOW_MS : null);
    return base === null ? null : base + SUGGESTION_TIMEOUT_GRACE_MS;
  }, [status, expectedByMs, expectedSince]);

  const refetch = useCallback(() => {
    if (encounterId) {
      dispatch(encountersApi.util.invalidateTags([{ type: 'Encounter' as const, id: encounterId }]));
    }
    if (noteId) {
      dispatch(notesApi.util.invalidateTags([{ type: 'Note' as const, id: noteId }]));
    }
  }, [dispatch, encounterId, noteId]);

  const shouldPoll =
    enabled &&
    isSupported &&
    status === 'expected' &&
    !isSuppressed &&
    !timedOut &&
    !arrivedOutOfBand &&
    Boolean(encounterId || noteId);

  useEffect(() => {
    if (!shouldPoll) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;

    const tick = (): void => {
      if (cancelled) return;
      if (deadlineMs !== null && Date.now() >= deadlineMs) {
        setTimedOut(true);
        return;
      }
      refetch();
      setPollCount(count => count + 1);
      schedule();
    };

    function schedule(): void {
      const index = Math.min(attempt, SUGGESTION_POLL_BACKOFF_MS.length - 1);
      attempt += 1;
      let delay: number = SUGGESTION_POLL_BACKOFF_MS[index];
      if (deadlineMs !== null) {
        // Never sleep past the deadline — the failure state must appear on time.
        delay = Math.min(delay, Math.max(0, deadlineMs - Date.now()));
      }
      timer = setTimeout(tick, delay);
    }

    schedule();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [shouldPoll, deadlineMs, refetch]);

  // Arrival transition: fire only on a genuine expected → ready flip that we
  // have not already played. `arrivedOutOfBand` is the dedupe — when the push
  // delivered the codes, the invalidation-driven flip to `ready` that follows
  // is the same arrival reported twice, not a second one.
  const previousStatusRef = useRef<SuggestionStatus | null>(null);
  useEffect(() => {
    const previous = previousStatusRef.current;
    previousStatusRef.current = status;

    if (previous !== 'expected' || status !== 'ready' || isSuppressed || arrivedOutOfBand) {
      return undefined;
    }

    setJustArrived(true);
    const timer = setTimeout(() => setJustArrived(false), SUGGESTION_ARRIVAL_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [status, isSuppressed, arrivedOutOfBand]);

  if (!isSupported) return INERT;

  return {
    isSupported,
    status,
    expectedByMs,
    deadlineMs,
    isPending: enabled && status === 'expected' && !isSuppressed && !timedOut && !arrivedOutOfBand,
    isTimedOut: timedOut && !isSuppressed && !arrivedOutOfBand,
    justArrived: justArrived && !isSuppressed,
    isSuppressed,
    arrivedOutOfBand,
    pollCount,
  };
}

export default useSuggestionExpectation;
